import { useCallback, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import DialogBox from '@/components/DialogBox';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import Attachments, { SubmitAttachment } from '@/services/attachments';
import { FetchApiIds } from '@/types';
import { FileCategories, UploadedCandidateAttachments } from '@/types/api';
import CandidateAttachments from './CandidateAttachments.component';
import { UploadLoadingType } from '../UploadLoading';

const CandidateAttachmentsContainer = function (): JSX.Element {
  const toast = useToastContext();
  const [uploadLoadingInfo, setUploadLoadingInfo] = useState<UploadLoadingType>({});
  const [documentIdToDelete, setDocumentIdToDelete] = useState('');
  const { id: candidateId } = useParams();
  const { profile } = useProfile();

  const createAttachmentMutation = useApiSubmit<void, SubmitAttachment>(
    Attachments.submitAttachment
  );

  const getAllCandidateAttachments = useApiFetch<UploadedCandidateAttachments, string>(
    [FetchApiIds.GET_ALL_ATTACHMENTS, candidateId],
    async () => Attachments.listAttachments(candidateId as string)
  );

  const formatFileSize = useCallback((fileSize: number): string => {
    if (fileSize === 0) return '0 Bytes';

    const kiloByte = 1024;
    const units = ['Bytes', 'Kb', 'Mb', 'Gb'];

    const formattedSize = Math.floor(Math.log(fileSize) / Math.log(kiloByte));

    return `${parseFloat((fileSize / kiloByte ** formattedSize).toFixed(2))} ${
      units[formattedSize]
    }`;
  }, []);

  const attachmentsFormatted = useMemo(() => {
    const attachments = getAllCandidateAttachments?.data ?? {};

    const categories = [
      { label: 'Resume', category: FileCategories.RESUME, id: 'RESUME' },
      {
        label: 'Formatted resume',
        category: FileCategories.FORMATTED_RESUME,
        id: 'FORMATTED_RESUME',
      },
      {
        label: 'Cover Letter',
        category: FileCategories.COVER_LETTER,
        id: 'COVER_LETTER',
      },
      { label: 'Others', category: FileCategories.OTHER, id: 'OTHER' },
      { label: 'Offer', category: FileCategories.OFFER, id: 'OFFER' },
      { label: 'Contracts', category: FileCategories.CONTRACT, id: 'CONTRACT' },
    ];

    const sortFilesByUpdatedAt = (a, b): number => {
      return new Date(a.updatedAt).valueOf() - new Date(b.updatedAt).valueOf();
    };

    return categories.map((category) => ({
      ...category,
      files: (attachments?.[category.id] ?? []).sort(sortFilesByUpdatedAt).map((file) => ({
        name: file?.name,
        attachedBy: file?.user?.name ?? '',
        updatedAt: format(new Date(file?.updatedAt), 'MMM dd') ?? '',
        size: formatFileSize(Number(file?.size)) ?? '',
        url: file?.url ?? '',
        id: file?.id ?? '',
      })),
    }));
  }, [formatFileSize, getAllCandidateAttachments?.data]);

  const onSelectFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
    category: string
  ): Promise<void> => {
    e.preventDefault();
    const selectedFiles = e.target.files;

    if (!selectedFiles) {
      return;
    }

    try {
      const payload = new FormData();
      [...selectedFiles].forEach((file) => payload.append('files', file));
      payload.append('category', category);
      payload.append('attachedBy', profile.id || '');
      setUploadLoadingInfo({
        id: category,
        totalOfLoadings: [...selectedFiles].length,
      });
      await createAttachmentMutation.mutateAsync({ payload, candidateId: candidateId as string });
      getAllCandidateAttachments.refetch();
      setUploadLoadingInfo({});
      toast.setMessage('Upload complete!');
      toast.setSeverity('success');
      toast.showToast();

      e.target.value = '';
    } catch (error: any) {
      setUploadLoadingInfo({});
      toast.setMessage('Error uploading file! Please, try again.');
      toast.setSeverity('error');
      toast.showToast();
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId: string): Promise<void> => {
    setDocumentIdToDelete(attachmentId);
  };

  const onConfirmDelete = async (attachmentId: string): Promise<void> => {
    try {
      await Attachments.deleteAttachment(candidateId as string, attachmentId);
      setDocumentIdToDelete('');
      getAllCandidateAttachments.refetch();
      toast.setMessage('Attachment removed successfully!');
      toast.setSeverity('success');
      toast.showToast();
    } catch (err) {
      toast.setMessage('There was an error removing the attachment. Please, try again.');
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  return (
    <>
      <DialogBox
        isOpen={!!documentIdToDelete}
        onCancelHandler={() => setDocumentIdToDelete('')}
        onConfirmHandler={() => onConfirmDelete(documentIdToDelete)}
      />
      <CandidateAttachments
        uploadLoadingInfo={{
          ...uploadLoadingInfo,
          isLoading: createAttachmentMutation.isLoading,
        }}
        attachments={attachmentsFormatted}
        onSelectFile={onSelectFile}
        handleDeleteAttachment={handleDeleteAttachment}
      />
    </>
  );
};

export default CandidateAttachmentsContainer;
